import { useState } from "react";
import PropTypes from "prop-types";

// Only the four properties that actually change between chip states — never a
// transition shorthand, which would sweep up transform and the ring too.
const CHIP_EASE = "var(--d-base) var(--ease-out)";
const CHIP_TRANSITION = [
  `border-color ${CHIP_EASE}`,
  `color ${CHIP_EASE}`,
  `background-color ${CHIP_EASE}`,
  `opacity ${CHIP_EASE}`,
].join(", ");

const BASE_CLASS =
  "rounded-full border px-4 py-2 text-sm font-medium " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand";

// Quiet until the user reaches for it. A row of chips is a question, not five
// calls to action, so brand colour is spent on the hover and on the answer.
const IDLE_CLASS = "border-line-strong bg-surface text-fg-secondary";
const HOVER_CLASS = "hover:border-brand hover:bg-brand-soft hover:text-brand";
const SELECTED_CLASS = "border-brand bg-brand text-brand-fg";
const INERT_CLASS = `${IDLE_CLASS} opacity-40 cursor-not-allowed`;

const ClarificationOptions = ({
  options,
  onOptionClick,
  disabled = false,
  selectedOption: externalSelectedOption = null,
}) => {
  const [localSelectedOption, setLocalSelectedOption] = useState(null);

  // Use external selectedOption if provided, otherwise use local state
  const currentSelectedOption = externalSelectedOption || localSelectedOption;

  const handleOptionClick = (option) => {
    if (disabled) return;

    // Only update local state if no external selection is provided
    if (!externalSelectedOption) {
      setLocalSelectedOption(option);
    }
    onOptionClick(option);
  };

  // Drives both the dimming and the disabled attribute, so a chip that looks
  // unavailable is genuinely unavailable to the keyboard too.
  const isInert = (option) =>
    disabled || (!!currentSelectedOption && currentSelectedOption !== option);

  const getButtonClassName = (option) => {
    if (currentSelectedOption === option) {
      return `${BASE_CLASS} ${SELECTED_CLASS}`;
    }
    if (isInert(option)) {
      return `${BASE_CLASS} ${INERT_CLASS}`;
    }
    return `${BASE_CLASS} ${IDLE_CLASS} ${HOVER_CLASS} active:scale-[0.97]`;
  };

  if (!options || options.length === 0) {
    return null;
  }

  return (
    <div className="clarification-options mt-3">
      <div className="flex flex-wrap gap-2">
        {options.map((option, index) => (
          <button
            key={`option-${index}-${option}`}
            className={getButtonClassName(option)}
            style={{ transition: CHIP_TRANSITION }}
            onClick={() => handleOptionClick(option)}
            aria-pressed={currentSelectedOption === option || undefined}
            disabled={isInert(option)}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
};

ClarificationOptions.propTypes = {
  options: PropTypes.arrayOf(PropTypes.string),
  onOptionClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  selectedOption: PropTypes.string,
};

export default ClarificationOptions;
